import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Schema } from 'zod';

import { CustomFormProvider } from '@/shared/components/ui/form/context/form-context';
import { StyledForm } from '@/shared/components/ui/form/form.styled';
import { TFormProps } from '@/shared/components/ui/form/form.types';
import { LoaderModal } from '@/shared/components/ui/loader-modal/loader-modal';
import { filterDirtyFields } from '@/shared/utils/form';
import { stripTypename } from '@/shared/utils/strip-typename';

const Form = <TFormValues extends FieldValues>({
  onSubmit,
  children,
  className,
  id,
  schema,
  defaultValues,
  loaderText,
  onSubmitted,
  resetAfterSubmit = true,
}: TFormProps<TFormValues, Schema>) => {
  // The compiler treats the dirtyFields read below as pure and drops it, losing the subscription.
  'use no memo';
  const [currentSchema, setCurrentSchema] = useState<Schema>(schema);
  const { t } = useTranslation();
  const sanitizedDefaultValues = useMemo(
    () => stripTypename(defaultValues),
    [defaultValues],
  );
  const methods = useForm<TFormValues>({
    resolver: zodResolver(currentSchema),
    mode: 'all',
    defaultValues: sanitizedDefaultValues,
  });
  // formState is a proxy: reading dirtyFields at render subscribes to it, else handleSubmit's read goes stale.
  const { dirtyFields: _dirtyFieldsSubscription } = methods.formState;

  useEffect(() => {
    if (!resetAfterSubmit || !methods.formState.isSubmitSuccessful) {
      return;
    }
    methods.reset(sanitizedDefaultValues);
  }, [methods.formState.isSubmitSuccessful]);

  const handleSubmit = async (data: TFormValues) => {
    const sanitizedData = stripTypename(data);
    const dirtyFields = methods.formState.dirtyFields;
    const dirtyData = stripTypename(
      filterDirtyFields<TFormValues>(sanitizedData, dirtyFields),
    );
    await onSubmit({ data: sanitizedData, dirtyData }, methods);
  };

  return (
    <CustomFormProvider
      schema={schema}
      currentSchema={currentSchema}
      setCurrentSchema={setCurrentSchema}
    >
      <FormProvider {...methods}>
        <StyledForm
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            // A rejected onSubmit keeps isSubmitSuccessful false (no reset);
            // swallow it here so it isn't an unhandled rejection.
            void methods
              .handleSubmit(handleSubmit)(event)
              .catch(() => {});
            onSubmitted?.();
          }}
          className={className}
          id={id}
        >
          <>
            <LoaderModal
              headline={t('ui:modal.headline')}
              open={methods.formState.isSubmitting}
              loaderText={loaderText ?? ''}
            />
            {children(methods)}
          </>
        </StyledForm>
      </FormProvider>
    </CustomFormProvider>
  );
};

export { Form };
